import React, { useEffect, useMemo, useState } from 'react';

import { fetchStaffList } from '../../services/api/staffApi';
import {
  addCoWorker,
  listCoWorkers,
} from '../../services/api/visitCoworkerApi';
import { formatStaffName } from '../../utils/staffName';

/**
 * CoWorkerPickerDialog — Phase 6.0 / 10.
 *
 * Lets the assigned doctor add another doctor as a co-worker on the
 * visit. Filters the staff dropdown to:
 *   - role=DOCTOR + status=ACTIVE
 *   - excludes the assigned doctor
 *   - excludes existing co-workers
 *
 * Props:
 *   visit          — visit row.
 *   onSubmitted    — (newCoWorker) => void  on successful add.
 *   onCancel       — () => void
 */
export default function CoWorkerPickerDialog({ visit, onSubmitted, onCancel }) {
  const [staff, setStaff] = useState([]);
  const [staffLoading, setStaffLoading] = useState(false);
  const [staffError, setStaffError] = useState(null);

  const [existingIds, setExistingIds] = useState(new Set());
  const [selected, setSelected] = useState('');
  const [busy, setBusy] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setStaffLoading(true);
    setStaffError(null);
    Promise.all([
      fetchStaffList(),
      listCoWorkers(visit.visit_id).catch(() => ({ co_workers: [] })),
    ]).then(([staffRes, cwRes]) => {
      if (cancelled) return;
      if (staffRes?.success) setStaff(staffRes.data || []);
      else setStaffError(staffRes?.error || 'Failed to load staff');
      const ids = new Set((cwRes?.co_workers || []).map((c) => c.member_id));
      setExistingIds(ids);
    }).finally(() => {
      if (!cancelled) setStaffLoading(false);
    });
    return () => { cancelled = true; };
  }, [visit?.visit_id]);

  const candidates = useMemo(() => {
    return (staff || [])
      .filter((m) => (m.role || '').toUpperCase() === 'DOCTOR')
      .filter((m) => (m.status || 'ACTIVE').toUpperCase() === 'ACTIVE')
      .filter((m) => m.member_id !== visit?.doctor_member_id)
      .filter((m) => !existingIds.has(m.member_id));
  }, [staff, visit?.doctor_member_id, existingIds]);

  const submit = async (e) => {
    e.preventDefault();
    if (!selected) return;
    setBusy(true);
    setErrorMsg(null);
    try {
      const created = await addCoWorker(visit.visit_id, selected);
      onSubmitted?.(created);
    } catch (err) {
      setErrorMsg(err?.response?.data?.detail || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <form
        onSubmit={submit}
        className="w-full max-w-md bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 rounded-lg shadow-xl p-5"
      >
        <h2 className="text-lg font-semibold mb-1">Add co-worker</h2>
        <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">
          Co-workers get full peer access on this visit — prescription,
          plan attach, action-pending hops. They appear in their own
          queue lane alongside owned visits.
        </p>

        <label className="block text-sm font-medium mb-1">Doctor *</label>
        {staffLoading ? (
          <div className="text-xs text-slate-500 dark:text-slate-400 py-2">
            Loading staff…
          </div>
        ) : staffError ? (
          <div className="text-xs text-rose-600 dark:text-rose-300 py-2">
            {staffError}
          </div>
        ) : (
          <select
            value={selected}
            onChange={(e) => setSelected(e.target.value)}
            className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
            required
          >
            <option value="">— choose —</option>
            {candidates.map((m) => (
              <option key={m.member_id} value={m.member_id}>
                {formatStaffName(m) || m.member_id}
              </option>
            ))}
          </select>
        )}
        {!staffLoading && candidates.length === 0 && (
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
            No eligible doctors — everyone active is either the assigned
            doctor or already a co-worker.
          </p>
        )}

        {errorMsg && (
          <div className="mt-3 rounded-md border border-rose-300 dark:border-rose-700 bg-rose-50 dark:bg-rose-900/30 px-3 py-2 text-xs text-rose-700 dark:text-rose-200">
            {errorMsg}
          </div>
        )}

        <div className="flex justify-end gap-2 mt-4">
          <button
            type="button"
            onClick={onCancel}
            className="px-3 py-1.5 text-sm rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 hover:bg-slate-50 dark:hover:bg-slate-700"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={busy || !selected}
            className="px-4 py-1.5 text-sm rounded-md bg-teal-600 hover:bg-teal-700 text-white disabled:opacity-60"
          >
            {busy ? 'Adding…' : 'Add co-worker'}
          </button>
        </div>
      </form>
    </div>
  );
}
